<script setup lang="ts">
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { ReserveType } from '@utils/itinerary';

const itineraryStore = useItineraryStore();
const { noResults } = storeToRefs(itineraryStore);
const searchFormStore = useSearchFormStore();
const { reserveType } = storeToRefs(searchFormStore);
const currentModule = useCurrentItineraryModule();
const flightModule = useFlightModule();
const flightForm = useFlightForm();
const { forecastPredicate, meta, averagePrice } = moduleToRefs(flightModule);
const route = useRoute();
const showForecast = computed(() => {
    if (reserveType.value !== ReserveType.Flight || flightForm.isForeign) {
        return false;
    }
    if (route.path.includes('IFN-THR') || route.path.includes('THR-IFN')) {
        return false;
    }
    if (
        [LoadingStatus.Loading, LoadingStatus.None, undefined].includes(
            currentModule.value.currentLoadingStatus
        )
    ) {
        return true;
    }
    return (
        !!meta.value?.AveragePrice &&
        !noResults.value &&
        !!itineraryStore.reservableList.length
    );
});
const iconSrc = computed(
    () => `/img/forecast/${priceType.value}-${forecastPredicate.value}.svg`
);
const showLoading = computed(
    () =>
        (reserveType.value === ReserveType.Flight && meta.value === null) ||
        itineraryStore.loading
);
const buyInfo = computed<{
    title: string;
    actionTitle?: string;
    description?: string;
}>(() => {
    if (showLoading.value) {
        return {
            title: 'درحال ارزیابی و پیش بینی قیمت ...',
        };
    }
    let actionTitle = '';
    let title: string;
    let description = '';
    switch (priceType.value) {
        case 'Expensive':
            switch (forecastPredicate.value) {
                case 'Buy':
                    actionTitle = 'هم‌اکنون بخرید!';
                    title = 'پیش‌بینی گران شدن قیمت‌ها';
                    description = `قیمت‌ها بالاتر از میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً افزایش می‌یابد.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت یا افزایش قیمت، خرید خود را نهایی کنید. با این حال با بررسی روزهای قبل و بعد ممکن است قیمت ارزان‌تری پیدا کنید.`;
                    break;
                case 'Wait':
                    actionTitle = 'به‌صرفه خرید کنید!';
                    title = 'ارزان‌تر شدن سفر با تغییر تاریخ';
                    description = `قیمت‌ها بالاتر از میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً کاهش می‌یابد.
با بررسی روزهای قبل و بعد ممکن است قیمت ارزان‌تری پیدا کنید. چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
                case 'Unknown':
                default:
                    title = 'قیمت‌ها بالا است';
                    description = `قیمت‌ها بالاتر از میانگین روزهای اخیر است.
با بررسی روزهای قبل و بعد ممکن است قیمت ارزان‌تری پیدا کنید. چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
            }
            break;
        case 'Cheap':
            switch (forecastPredicate.value) {
                case 'Buy':
                    actionTitle = 'هم‌اکنون بخرید!';
                    title = 'پیش‌بینی افزایش قیمت‌ها';
                    description = `قیمت‌ها ارزان‌تر از میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً افزایش می‌یابد.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت یا افزایش قیمت، خرید خود را نهایی کنید.`;
                    break;
                case 'Wait':
                    actionTitle = 'هم‌اکنون بخرید!';
                    title = 'زمان مناسب برای سفری به‌صرفه';
                    description = `قیمت‌ها ارزان‌تر از میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً کاهش می‌یابد.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت، خرید خود را نهایی کنید یا چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
                case 'Unknown':
                default:
                    title = 'قیمت‌ها ارزان است';
                    description = `قیمت‌ها ارزان‌تر از میانگین روزهای اخیر است.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت، خرید خود را نهایی کنید یا چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
            }
            break;
        case 'Average':
            switch (forecastPredicate.value) {
                case 'Buy':
                    actionTitle = 'هم‌اکنون بخرید!';
                    title = 'پیش‌بینی افزایش قیمت‌ها';
                    description = `قیمت‌ها نزدیک به میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً افزایش می‌یابد.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت یا افزایش قیمت، خرید خود را نهایی کنید.`;
                    break;
                case 'Wait':
                    actionTitle = 'هم‌اکنون بخرید!';
                    title = 'زمان مناسب برای سفری به‌صرفه';
                    description = `قیمت‌ها نزدیک به میانگین روزهای اخیر است و بر اساس پیش‌بینی مِستربلیط، احتمالاً کاهش می‌یابد.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت، خرید خود را نهایی کنید یا چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
                case 'Unknown':
                default:
                    title = 'قیمت‌ها خوب است';
                    description = `قیمت‌ها نزدیک به میانگین روزهای اخیر است.
پیشنهاد می‌کنیم قبل از تکمیل ظرفیت، خرید خود را نهایی کنید یا چنانچه نگران تکمیل ظرفیت نیستید، با ثبت هشدار، منتظر کاهش قیمت بمانید.
            `;
                    break;
            }
            break;
        default:
            title = '';
            break;
    }
    return { title, actionTitle, description };
});
const Q1 = computed(() => meta.value?.AveragePrice?.FirstQuartile || 0);
const Q1Formatted = computed(
    () =>
        `${formatMoney(Q1.value, {
            unit: 'KToman',
        })} تومانء`
);
const Q3 = computed(() => meta.value?.AveragePrice?.ThirdQuartile || 0);
const Q3Formatted = computed(
    () =>
        `${formatMoney(Q3.value, {
            unit: 'KToman',
        })} تومانء`
);
const min = computed(() => meta.value?.AveragePrice?.MinPrice || 0);
const max = computed(() => meta.value?.AveragePrice?.MaxPrice || 0);
const priceType = computed(() => {
    if (min.value < averagePrice.value && averagePrice.value < Q1.value) {
        return 'Cheap';
    } else if (Q1.value < averagePrice.value && averagePrice.value < Q3.value) {
        return 'Average';
    } else {
        return 'Expensive';
    }
});
const averageRight = computed(() => {
    if (averagePrice.value > max.value) {
        return 100;
    }
    if (averagePrice.value < min.value) {
        return 0;
    }
    if (averagePrice.value < Q1.value) {
        return ((averagePrice.value - min.value) / (Q1.value - min.value)) * 25;
    }
    if (Q3.value > averagePrice.value && averagePrice.value >= Q1.value) {
        return (
            ((averagePrice.value - Q1.value) / (Q3.value - Q1.value)) * 50 + 25
        );
    }
    if (averagePrice.value >= Q3.value) {
        return (
            ((averagePrice.value - Q3.value) / (max.value - Q3.value)) * 25 + 75
        );
    }
    return 0;
});
</script>

<template>
    <mr-accordion
        v-if="showForecast"
        class="forecast-container"
        :disabled="showLoading"
        preserve-state-on-disable
        :header-styles="{ paddingInline: '16px' }"
    >
        <template #extra-header>
            <div v-if="buyInfo.actionTitle" class="action-title">
                {{ buyInfo.actionTitle }}
            </div>
        </template>
        <template #title>
            <div class="title-container">
                <img alt="icon" :src="iconSrc" />
                <h3>{{ buyInfo.title }}</h3>
            </div>
        </template>
        <template #toggle="{ currentIcon }">
            <mr-button
                v-show="!showLoading"
                :icon-button="{ icon: currentIcon, color: 'var(--gray-900)' }"
                size="small"
                variant="transparent"
                class="toggle-btn"
            />
        </template>
        <div class="body">
            <p>{{ buyInfo.description }}</p>
            <div class="line-container">
                <div class="line green" />
                <div class="line yellow">
                    <div class="small-circle">
                        <div class="label">
                            {{ Q1Formatted }}
                        </div>
                    </div>
                </div>
                <div class="line red">
                    <div class="small-circle">
                        <div class="label">
                            {{ Q3Formatted }}
                        </div>
                    </div>
                </div>
                <div
                    class="marker-triangle"
                    :style="{ right: `${averageRight}%` }"
                >
                    <img
                        src="~/assets/files/forecast-triangle.svg"
                        alt="triangle"
                    />
                </div>
            </div>
        </div>
    </mr-accordion>
</template>

<style scoped lang="scss">
@include v-transition-opacity;
$avg-green: usePalette($p-green);
$avg-yellow: usePalette($p-cool-gray, $p-200);
$avg-red: usePalette($p-red);
.action-title {
    @include tg-m($w7);
}
.forecast-container {
    position: relative;
    background-color: white;
    border-radius: 0;
    border: none;
    padding-top: 12px;
    .title-container {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        h3 {
            @include tg-m($w5);
            padding-left: 8px;
            padding-right: 8px;
            margin: 0;
        }
    }
    .toggle-btn {
        margin-bottom: 12px;
    }
    .body {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        background-color: usePalette($p-cool-gray, $p-100);
        padding: 16px 16px 8px;

        p {
            margin: 0;
            @include tg-s;
        }
        .line-container {
            position: relative;
            display: flex;
            width: calc(100% - 10px);
            margin: 24px auto 43px;
            padding-inline: 4px;
            .line {
                height: 5px;
                border-radius: 10px;
                position: relative;
                &.red {
                    flex: 1;
                    background: $avg-red;
                    .small-circle {
                        background-color: $avg-red;
                        .label {
                            color: $avg-red;
                        }
                    }
                }
                &.yellow {
                    flex: 2;
                    background: $avg-yellow;
                    margin-inline: 12px;
                    .small-circle {
                        background-color: $avg-green;
                        .label {
                            color: $avg-green;
                        }
                    }
                }
                &.green {
                    flex: 1;
                    background: $avg-green;
                }
            }
            .marker-triangle {
                position: absolute;
                top: -5px;
                transform: translateX(50%);
                z-index: 10;
            }
        }
    }
}
.small-circle {
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 100%;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    right: -10px;

    .label {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        bottom: -25px;
        left: 50%;
        transform: translateX(-50%);
        // text
        text-align: right;
        white-space: nowrap;
        @include tg-s($w7, usePalette());
    }
}
</style>
